import { displayAuthorName } from "@/lib/commentLabels";
import { relativeTime, cn } from "@/lib/utils";
import PlatformAvatar from "./PlatformAvatar";
import {
  normalizeCommentPlatform,
  type NativeCommentViewModel,
} from "./nativeCommentModel";

type Props = {
  model: NativeCommentViewModel;
  mode?: "native" | "landing";
};

function displayName(model: NativeCommentViewModel): string {
  if (model.displayHandle) return model.displayHandle;
  return displayAuthorName(model.author, model.sourceKind || model.platform);
}

function IgComment({ model }: { model: NativeCommentViewModel }) {
  const name = displayName(model);
  return (
    <div className="rounded-2xl bg-white px-3 py-3">
      <div className="flex gap-3">
        <PlatformAvatar name={model.author || name} avatarUrl={model.avatarUrl} platform="instagram" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug text-slate-900">
            <span className="font-semibold">{name.replace(/^@/, "")}</span>{" "}
            <span className="font-normal whitespace-pre-wrap">{model.text}</span>
          </p>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-400">
            {model.createdAt && <span>{relativeTime(model.createdAt)}</span>}
            {(model.likeCount || 0) > 0 && <span>{model.likeCount} polubień</span>}
            <span>Odpowiedz</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FbComment({ model }: { model: NativeCommentViewModel }) {
  const name = displayName(model);
  return (
    <div className="rounded-2xl bg-[#f0f2f5] px-3 py-3">
      <div className="flex gap-2.5">
        <PlatformAvatar name={model.author || name} avatarUrl={model.avatarUrl} platform="facebook" size="sm" />
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl bg-white px-3 py-2 shadow-sm">
            <div className="text-[13px] font-semibold text-slate-900">{name}</div>
            <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-snug text-slate-800">
              {model.text}
            </p>
          </div>
          <div className="mt-1 flex gap-3 px-1 text-[11px] font-semibold text-slate-500">
            <span>Lubię to</span>
            <span>Odpowiedz</span>
            {model.createdAt && (
              <span className="font-normal text-slate-400">{relativeTime(model.createdAt)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DmBubble({ model }: { model: NativeCommentViewModel }) {
  const name = displayName(model);
  const isIg =
    model.sourceKind === "dm_ig" ||
    normalizeCommentPlatform(model.platform, model.sourceKind) === "instagram";
  return (
    <div
      className={cn(
        "rounded-2xl px-3 py-3",
        isIg ? "bg-gradient-to-b from-slate-900 to-slate-800" : "bg-[#eef2ff]",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <PlatformAvatar
          name={model.author || name}
          avatarUrl={model.avatarUrl}
          platform={isIg ? "instagram" : "messenger"}
          size="sm"
        />
        <div className={cn("text-[12px] font-semibold", isIg ? "text-white" : "text-slate-800")}>
          {name}
        </div>
      </div>
      <div
        className={cn(
          "max-w-[92%] rounded-2xl rounded-tl-md px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap",
          isIg ? "bg-slate-700 text-white" : "bg-white text-slate-900 shadow-sm",
        )}
      >
        {model.text}
      </div>
      {model.createdAt && (
        <div className={cn("mt-1.5 text-[10px]", isIg ? "text-slate-400" : "text-slate-500")}>
          {relativeTime(model.createdAt)}
        </div>
      )}
    </div>
  );
}

function TikTokComment({ model }: { model: NativeCommentViewModel }) {
  const name = displayName(model).replace(/^@/, "");
  return (
    <div className="rounded-2xl bg-white px-3 py-3">
      <div className="flex gap-3">
        <PlatformAvatar name={model.author || name} avatarUrl={model.avatarUrl} platform="tiktok" />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-slate-500">{name}</div>
          <p className="mt-0.5 whitespace-pre-wrap text-[14px] leading-snug text-slate-900">
            {model.text}
          </p>
          <div className="mt-1.5 flex items-center gap-4 text-[11px] text-slate-400">
            {model.createdAt && <span>{relativeTime(model.createdAt)}</span>}
            <span>Odpowiedź</span>
            {(model.likeCount || 0) > 0 && <span>{model.likeCount}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function YouTubeComment({ model }: { model: NativeCommentViewModel }) {
  const name = displayName(model);
  return (
    <div className="rounded-2xl bg-white px-3 py-3">
      <div className="flex gap-3">
        <PlatformAvatar name={model.author || name} avatarUrl={model.avatarUrl} platform="youtube" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[13px] font-medium text-slate-900">{name}</span>
            {model.createdAt && (
              <span className="text-[11px] text-slate-400">{relativeTime(model.createdAt)}</span>
            )}
          </div>
          <p className="mt-1 whitespace-pre-wrap text-[13px] leading-snug text-slate-800">
            {model.text}
          </p>
          <div className="mt-2 flex items-center gap-4 text-[11px] font-medium text-slate-500">
            {(model.likeCount || 0) > 0 ? <span>👍 {model.likeCount}</span> : <span>👍</span>}
            <span>Odpowiedz</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingQuote({ model }: { model: NativeCommentViewModel }) {
  const isDm = String(model.sourceKind || "").startsWith("dm_");
  return (
    <blockquote className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <p className="text-[15px] leading-relaxed text-slate-800">„{model.text}”</p>
      <footer className="mt-3 text-xs font-medium text-slate-500">
        {isDm ? "Czytelnaczka" : displayName(model)}
        {model.proofLabel ? ` · ${model.proofLabel}` : ""}
      </footer>
    </blockquote>
  );
}

export default function NativeCommentCard({ model, mode = "native" }: Props) {
  if (mode === "landing") return <LandingQuote model={model} />;
  const sk = String(model.sourceKind || "").toLowerCase();
  if (sk === "dm_fb" || sk === "dm_ig") return <DmBubble model={model} />;
  const platform = normalizeCommentPlatform(model.platform, model.sourceKind);
  if (platform === "tiktok") return <TikTokComment model={model} />;
  if (platform === "youtube") return <YouTubeComment model={model} />;
  if (platform === "facebook") return <FbComment model={model} />;
  return <IgComment model={model} />;
}
